import { each } from "underscore";

import { NETWORK_STATES, REPLACED } from "workspace/util/networkManager";
import { defaultUrls } from "workspace/util/defaults";

import { defaultSettings } from "wxrecall/util/defaults";

// TODO: Many much of this is extremely similar or even identical to the
// `workspace/util/networkManager` class. We should really merge them as much as possible, but for
// now just making it work

const $ = window.$;
const maxRetries = 5;
const defaultDelay = 1000; // Max of one request per second
const urls = window.urls || defaultUrls;
const settings = window.settings || defaultSettings;

class NetworkManager {
  // This map will store the last urls that were used for each request type
  lastRequests = new Map();

  constructor(updateRequestState, delay = defaultDelay) {
    // Start with an empty dictionary of requests
    this.requests = {};

    // We have a couple of different types, which we use to populate the `requests`
    this.requestTypes = {
      weatherSuggestion: {
        url:
          urls["search-suggestion"] +
          `/${settings.language}?include_polygons=true&filter[value]=`
      },
      weatherSearch: {
        url: urls["wxrecall"]
      },
      bulletinSearch: {
        url: urls["bulletinRecall"]
      },
      save: {
        url: urls["save_recall"],
        type: "POST",
        contentType: "application/json; charset=utf-8"
      },
      saveSettings: {
        url: urls["save_settings"],
        type: "POST",
        contentType: "application/json; charset=utf-8"
      },
      delete: {
        url: urls["delete_recall"],
        type: "DELETE",
        contentType: "application/json; charset=utf-8"
      },
      fetchImage: {
        type: "GET",
        contentType: false,
        cache: true,
        unique: true
      }
    };

    // Keep track of activeRequests, which includes number of failures, the timeout ids, and the
    // jqXHR object, so we can cancel them if need be
    this.activeRequests = {};

    // Make this.requests.{requestType} a function to make a network request
    each(
      this.requestTypes,
      (defaultArgs, requestType) => {
        this.requests[requestType] = this.sendRequest.bind(
          this,
          requestType,
          defaultArgs
        );
      },
      this
    );

    // Keep a copy of the passed-in function to update the state of the requests, so we can display
    // stuff like network loading and error pages.
    this.updateRequestState = updateRequestState;

    this.delay = delay;
  }

  _resetRequestState = requestType => {
    // Tell our "parent" component that the network state is pending, so we can display a loading
    // indicator
    this.updateRequestState(requestType, "state", NETWORK_STATES.PENDING);

    // Reset any data for this type of network request
    this.activeRequests[requestType] = {
      requestErrorCount: 0,
      jqXHR: null,
      timeout: null
    };
  };

  sendRequest = (requestType, defaultArgs, extraArgs) => {
    // Properly handle any previous requests of this type
    const previousRequest = this.activeRequests[requestType];

    if (previousRequest) {
      // Clear out any pending requests for this type
      // This will fail silently if we don't have a valid timeout
      if (!this.requestTypes[requestType].unique) {
        // we don't want to cancel image requests - stops playback on other panels
        clearTimeout(previousRequest.timeout);
        // Cancel any active requests, if they exist
        if (previousRequest.jqXHR) {
          previousRequest.jqXHR.abort(REPLACED);
        }
      }
    }

    let args = {
      cache: false,
      ...defaultArgs
    };

    if (extraArgs) {
      // Handle different types of extra arguments
      if (typeof extraArgs === "string") {
        args.url += extraArgs;
      } else if (typeof extraArgs === "object") {
        args = {
          ...args,
          ...extraArgs
        };
      }
    }

    this._resetRequestState(requestType);

    // Have a "transaction" promise we return to the calling code. This will be resolved if the
    // request succeeds. However, if it fails, we will retry {maxRetries} times before rejecting it
    let transactionPromise = new Promise((resolve, reject) => {
      this._sendRequest(resolve, reject, args, requestType);
    });

    return transactionPromise;
  };

  _sendRequest = (resolve, reject, args, requestType) => {
    const jqXHR = $.ajax(args)
      .done(response => {
        this.lastRequests.set(requestType, args.url);
        this.updateRequestState(requestType, "state", NETWORK_STATES.FETCHED);
        this.updateRequestState(requestType, "transactionError", false);
        resolve(response);
      })
      .fail((jqXHR, textStatus) => {
        // If we didn't intentionally replace this network request
        if (jqXHR.statusText !== REPLACED) {
          this._retryOrReject(
            resolve,
            reject,
            args,
            requestType,
            jqXHR,
            textStatus
          );
        }
      });
    this.updateRequestState(requestType, "state", NETWORK_STATES.FETCHING);
    this.activeRequests[requestType].jqXHR = jqXHR;
  };

  _retryOrReject = (resolve, reject, args, requestType, jqXHR, textStatus) => {
    ++this.activeRequests[requestType].requestErrorCount;
    // Check to see if we already have retried enough times
    if (this.activeRequests[requestType].requestErrorCount >= maxRetries) {
      this.updateRequestState(requestType, "transactionError", true);
      this.updateRequestState(requestType, "state", NETWORK_STATES.ERROR);
      // TODO: Make this reject it with the full response content
      // TODO: This doesn't appear to be handled at all after the very first one; we should have the
      //       calling code pay attention to it
      reject(
        "Failed after " +
          maxRetries +
          " retries" +
          (textStatus ? " with status of: " + textStatus : "")
      );

      // We want to retry this request in 30 seconds, to jump us out of "error" mode, if possible
      this.activeRequests[requestType].timeout = setTimeout(() => {
        this.sendRequest(requestType, args);
      }, 30 * 1000);
    } else {
      this.activeRequests[requestType].timeout = setTimeout(() => {
        this._sendRequest(resolve, reject, args, requestType);
      }, this.delay * this.activeRequests[requestType].requestErrorCount);
    }
  };
}

export default NetworkManager;
